/**
 * SL-IMG-1 — Supply Lab branded image placeholder.
 *
 * Every un-imaged supply (and every supply whose image fails to load) renders THIS instead of a
 * grey lucide Package glyph, so a missing photo reads as intentional brand surface, not as a
 * broken site. Inline SVG only — zero network, zero lucide import (the supply-lab test suites
 * mock lucide-react as CLOSED enumerations; a new icon import would crash them).
 *
 * Brand: orange #F97316 on zinc-900 #18181b (operator spec). Category-aware glyph chosen by
 * KEYWORD, never an exact map — the served category vocabulary is free text and can be "".
 *
 * A11y: the wrapper is role="img" with a full aria-label; every SVG <text> is aria-hidden so
 * screen readers hear the label once. No role="button" is ever added (the card that mounts this
 * is itself the button).
 *
 * Variants: card (aspect-video) · hero (aspect-[4/3]) · thumb (60x60, glyph only) · cart (compact).
 */

const ORANGE = '#F97316';
const ZINC_900 = '#18181b';
const ZINC_700 = '#3f3f46';
const ZINC_400 = '#a1a1aa';
const ZINC_200 = '#e4e4e7';
const MONO = 'JetBrains Mono, Consolas, monospace';

export const GLYPHS = ['bag', 'board', 'bin', 'case', 'default'];

/** Keyword -> glyph key, or null when the text carries no keyword. Order matters only where
 *  a string could carry two keywords ("Comic Book Bin Partitions" is a bin). */
function keywordGlyph(text) {
  const c = String(text || '').toLowerCase();
  if (c.includes('bag')) return 'bag';
  if (c.includes('board')) return 'board';
  if (c.includes('bin') || c.includes('partition') || c.includes('divider')) return 'bin';
  if (c.includes('case') || c.includes('loader')) return 'case';
  return null;
}

/** Category-aware glyph by keyword, never an exact map. The category decides; when it carries
 *  no keyword (empty, unknown, or the cart's literal 'supplies' taxonomy) the product NAME is
 *  consulted so a glyph-only variant still says what the thing is. Nothing matches -> default. */
export function glyphFor(category, name = '') {
  return keywordGlyph(category) || keywordGlyph(name) || 'default';
}

/** Single-line SVG text does not wrap; keep names legible by truncating. */
export function truncate(value, max) {
  const s = String(value || '');
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s;
}

// Layout table per variant. Unknown variant -> card.
const LAYOUTS = {
  card: { w: 320, h: 180, glyph: { cx: 160, cy: 64, size: 46 }, text: { cat: 116, name: 138, tag: 163, nameSize: 13, nameMax: 34 } },
  hero: { w: 400, h: 300, glyph: { cx: 200, cy: 112, size: 84 }, text: { cat: 194, name: 220, tag: 254, nameSize: 17, nameMax: 40 } },
  thumb: { w: 60, h: 60, glyph: { cx: 30, cy: 30, size: 34 }, text: null },
  cart: { w: 80, h: 96, glyph: { cx: 40, cy: 44, size: 40 }, text: null },
};

function Glyph({ kind, cx, cy, size }) {
  const s = size / 2;
  const common = { fill: 'none', stroke: ORANGE, strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' };
  switch (kind) {
    case 'bag':
      // A polybag: tall sleeve with a fold-over flap and a comic outline inside.
      return (
        <g {...common}>
          <rect x={cx - s * 0.7} y={cy - s} width={s * 1.4} height={s * 2} rx={3} />
          <path d={`M${cx - s * 0.7} ${cy - s * 0.55} H${cx + s * 0.7}`} />
          <path d={`M${cx - s * 0.7} ${cy - s} l${s * 0.7} ${s * 0.32} l${s * 0.7} ${-s * 0.32}`} />
          <rect x={cx - s * 0.42} y={cy - s * 0.3} width={s * 0.84} height={s * 1.1} rx={1.5} strokeOpacity={0.55} />
        </g>
      );
    case 'board':
      // A backing board: a stiff rectangle with diagonal grain.
      return (
        <g {...common}>
          <rect x={cx - s * 0.72} y={cy - s} width={s * 1.44} height={s * 2} rx={2} />
          <path d={`M${cx - s * 0.5} ${cy + s * 0.8} L${cx + s * 0.3} ${cy - s * 0.8}`} strokeOpacity={0.5} />
          <path d={`M${cx - s * 0.1} ${cy + s * 0.8} L${cx + s * 0.55} ${cy - s * 0.3}`} strokeOpacity={0.5} />
          <path d={`M${cx - s * 0.6} ${cy + s * 0.2} L${cx - s * 0.1} ${cy - s * 0.8}`} strokeOpacity={0.5} />
        </g>
      );
    case 'bin':
      // A storage bin: open box with vertical partitions.
      return (
        <g {...common}>
          <path d={`M${cx - s} ${cy - s * 0.45} L${cx + s} ${cy - s * 0.45} L${cx + s * 0.82} ${cy + s * 0.9} L${cx - s * 0.82} ${cy + s * 0.9} Z`} />
          <path d={`M${cx - s * 0.36} ${cy - s * 0.45} V${cy + s * 0.9}`} strokeOpacity={0.6} />
          <path d={`M${cx + s * 0.36} ${cy - s * 0.45} V${cy + s * 0.9}`} strokeOpacity={0.6} />
          <path d={`M${cx - s * 0.7} ${cy - s * 0.9} H${cx + s * 0.7}`} />
        </g>
      );
    case 'case':
      // A latching case: closed shell, handle on top, two latches on the seam.
      return (
        <g {...common}>
          <rect x={cx - s} y={cy - s * 0.5} width={s * 2} height={s * 1.3} rx={4} />
          <path d={`M${cx - s * 0.3} ${cy - s * 0.5} V${cy - s * 0.75} a${s * 0.3} ${s * 0.25} 0 0 1 ${s * 0.6} 0 V${cy - s * 0.5}`} />
          <path d={`M${cx - s} ${cy + s * 0.05} H${cx + s}`} strokeOpacity={0.6} />
          <rect x={cx - s * 0.62} y={cy - s * 0.1} width={s * 0.22} height={s * 0.3} rx={1} />
          <rect x={cx + s * 0.4} y={cy - s * 0.1} width={s * 0.22} height={s * 0.3} rx={1} />
        </g>
      );
    default:
      // A supply box: an isometric cube.
      return (
        <g {...common}>
          <path d={`M${cx} ${cy - s} L${cx + s * 0.9} ${cy - s * 0.5} V${cy + s * 0.5} L${cx} ${cy + s} L${cx - s * 0.9} ${cy + s * 0.5} V${cy - s * 0.5} Z`} />
          <path d={`M${cx - s * 0.9} ${cy - s * 0.5} L${cx} ${cy} L${cx + s * 0.9} ${cy - s * 0.5}`} strokeOpacity={0.7} />
          <path d={`M${cx} ${cy} V${cy + s}`} strokeOpacity={0.7} />
        </g>
      );
  }
}

export default function SupplyImagePlaceholder({ name = '', category = '', variant = 'card', className = '' }) {
  const layout = LAYOUTS[variant] || LAYOUTS.card;
  const resolvedVariant = LAYOUTS[variant] ? variant : 'card';
  const glyph = glyphFor(category, name);
  const cat = String(category || '').trim();
  const label = cat
    ? `${name} — ${cat} — image coming soon`
    : `${name} — image coming soon`;

  return (
    <div
      role="img"
      aria-label={label}
      data-testid="supply-image-placeholder"
      data-variant={resolvedVariant}
      data-glyph={glyph}
      className={`flex items-center justify-center overflow-hidden ${className || 'w-full h-full'}`}
      style={{ backgroundColor: ZINC_900 }}
    >
      <svg
        viewBox={`0 0 ${layout.w} ${layout.h}`}
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0" y="0" width={layout.w} height={layout.h} fill={ZINC_900} />
        {/* brand accent bar */}
        <rect x="0" y="0" width={layout.w} height="3" fill={ORANGE} />
        {/* hairline frame */}
        <rect x="1" y="1" width={layout.w - 2} height={layout.h - 2} fill="none" stroke={ZINC_700} strokeWidth="1" />
        <Glyph kind={glyph} cx={layout.glyph.cx} cy={layout.glyph.cy} size={layout.glyph.size} />
        {layout.text && (
          <>
            {cat && (
              <text
                aria-hidden="true"
                x={layout.w / 2}
                y={layout.text.cat}
                textAnchor="middle"
                fontFamily={MONO}
                fontSize="10"
                letterSpacing="2"
                fill={ORANGE}
                fillOpacity="0.85"
              >
                {truncate(cat.toUpperCase(), 30)}
              </text>
            )}
            <text
              aria-hidden="true"
              x={layout.w / 2}
              y={layout.text.name}
              textAnchor="middle"
              fontFamily={MONO}
              fontSize={layout.text.nameSize}
              fontWeight="600"
              fill={ZINC_200}
            >
              {truncate(name, layout.text.nameMax)}
            </text>
            <text
              aria-hidden="true"
              x={layout.w / 2}
              y={layout.text.tag}
              textAnchor="middle"
              fontFamily={MONO}
              fontSize="9"
              letterSpacing="3"
              fill={ZINC_400}
            >
              IMAGE COMING SOON
            </text>
          </>
        )}
      </svg>
    </div>
  );
}
